/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { ReactNode } from 'react';
import type { SnippetsSectionProps } from '../Snippets/types';
import { getCKEditorZipUrl } from '../../../utils/getCKEditorZipUrl/getCKEditorZipUrl';
import { HighlightCode } from '../../HighlightCode/HighlightCode';
import { SkeletonLoader } from '../SkeletonLoader/SkeletonLoader';
import { SnippetsSection } from '../Snippets/SnippetsSection';
import { Text } from './Text';
import { SECTION_NAME_DOWNLOAD_FILES } from '../../../constants/snippetsSectionNames';

export function JsZipDownload( {
	hasPremiumPlugins,
	isLoading,
	number,
	onCopyEvent
}: SnippetsSectionProps ): ReactNode {
	return (
		<SkeletonLoader number={ number } sectionTitle={ SECTION_NAME_DOWNLOAD_FILES } isLoading={ isLoading }>
			<SnippetsSection headerCharacter={ number } title={ SECTION_NAME_DOWNLOAD_FILES }>
				<Text>
					{ 'To begin, download the ' }
					{
						hasPremiumPlugins && (
							<a
								rel="noopener noreferrer"
								href={ getCKEditorZipUrl( 'ckeditor5-premium-features' ) }
								data-testid={ 'ckeditor5-self-hosted-zip' }
								target={ '_blank' }
							>
								CKEditor 5 with Premium Features
							</a>
						)
					}
					{
						!hasPremiumPlugins && (
							<a
								rel="noopener noreferrer"
								href={ getCKEditorZipUrl( 'ckeditor5' ) }
								data-testid={ 'ckeditor5-self-hosted-zip' }
								target={ '_blank' }
							>
								CKEditor 5
							</a>
						)
					}
					{
						' ZIP file. Create a folder, and unpack the archive there in the following structure:'
					}
				</Text>
				<HighlightCode onCopyEvent={ onCopyEvent } lang={ 'plaintext' }>
					{
						hasPremiumPlugins ?
							[
								'my-editor/',
								'├── ckeditor5/',
								'├── ckeditor5-premium-features/',
								'├── index.html',
								'├── main.js',
								'└── style.css'
							].join( '\n' ) :
							[
								'my-editor/',
								'├── ckeditor5/',
								'├── index.html',
								'├── main.js',
								'└── style.css'
							].join( '\n' )
					}
				</HighlightCode>
			</SnippetsSection>
		</SkeletonLoader>
	);
}
